<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  totals: {
    type: Object,
    default: () => ({
      totalCustomers: 0,
      completed: 0,
      inProgress: 0,
      pending: 0,
    }),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const cards = computed(() => {
  const total = props.totals.totalCustomers || 0;
  const percent = value => {
    if (!total) {
      return '0%';
    }
    return `${Math.round((value / total) * 100)}%`;
  };

  return [
    {
      key: 'totalCustomers',
      label: t('DATA_COLLECTION_REPORTS.SUMMARY.TOTAL'),
      value: props.totals.totalCustomers || 0,
      accent: 'from-primary-100 to-primary-50 dark:from-primary-800 dark:to-primary-900',
      showPercent: false,
    },
    {
      key: 'completed',
      label: t('DATA_COLLECTION_REPORTS.SUMMARY.COMPLETED'),
      value: props.totals.completed || 0,
      accent: 'from-emerald-100 to-emerald-50 dark:from-emerald-800 dark:to-emerald-900',
      showPercent: true,
      percent: percent(props.totals.completed || 0),
    },
    {
      key: 'inProgress',
      label: t('DATA_COLLECTION_REPORTS.SUMMARY.IN_PROGRESS'),
      value: props.totals.inProgress || 0,
      accent: 'from-amber-100 to-amber-50 dark:from-amber-800 dark:to-amber-900',
      showPercent: true,
      percent: percent(props.totals.inProgress || 0),
    },
    {
      key: 'pending',
      label: t('DATA_COLLECTION_REPORTS.SUMMARY.PENDING'),
      value: props.totals.pending || 0,
      accent: 'from-n-gray-1 to-n-gray-0 dark:from-n-gray-6 dark:to-n-gray-7',
      showPercent: true,
      percent: percent(props.totals.pending || 0),
    },
  ];
});
</script>

<template>
  <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
    <div
      v-for="card in cards"
      :key="card.key"
      class="rounded-2xl border border-n-weak bg-gradient-to-br p-4 shadow-sm dark:border-white/10"
      :class="card.accent"
    >
      <p class="text-sm font-medium text-n-gray-6 dark:text-n-gray-3">
        {{ card.label }}
      </p>
      <div class="mt-2 flex items-end justify-between">
        <p class="text-3xl font-semibold text-n-slate-12 dark:text-white">
          <Spinner v-if="isLoading" size="16" />
          <span v-else>{{ card.value }}</span>
        </p>
        <span
          v-if="card.showPercent"
          class="text-sm font-semibold text-n-gray-5 dark:text-n-gray-3"
        >
          {{ card.percent }}
        </span>
      </div>
    </div>
  </div>
</template>
